import React, { useState, useEffect, cloneElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/utils/cn";
import { X } from "lucide-react";
import { customDigitalHumanHooks, customDigitalHumanManager } from "@/manager/manager/custom-digitalhuman-manager";
import { StepIndicator } from "./step-indicator";
import { UploadForm } from "./upload-form";
import { VerifyForm } from "./verify-form";
import { CreateForm } from "./create-form";
import { toast } from "sonner";

// 定义弹窗状态
type ModalStep = 0 | 1 | 2 | 3;

interface UploadModalProps {
  // trigger 模式
  trigger?: React.ReactElement;
  // open 参数模式
  open?: boolean;
  uploadType: "image" | "video";
  // 远程 url
  remoteUrl?: string;
  onOpenChange?: (open: boolean) => void;
  onCreate?: (data: { actorId: string; voiceId: string }) => void;
  onCreateSuccess?: () => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  trigger,
  open: externalOpen,
  uploadType,
  remoteUrl: initialRemoteUrl,
  onOpenChange,
  onCreate,
  onCreateSuccess,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [remoteUrl, setRemoteUrl] = useState(initialRemoteUrl);
  const [currentStep, setCurrentStep] = useState<ModalStep>(0);

  const { upload, setFileUri } = customDigitalHumanHooks.useCustomDigitalHuman(uploadType);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }

    // 重置状态
    if (!open) {
      setCurrentStep(0);
    }
  };

  // 在 open 参数模式下，同步外部 open 状态到内部状态
  useEffect(() => {
    if (!isTriggerMode && externalOpen !== undefined) {
      setInternalOpen(externalOpen);
    }
  }, [externalOpen, isTriggerMode]);

  useEffect(() => {
    if (initialRemoteUrl) {
      setRemoteUrl(initialRemoteUrl);
      setFileUri(initialRemoteUrl);
      // setCurrentStep(2);
    }
  }, [initialRemoteUrl]);

  // 上传文件
  const handleUpload = async (file: File) => {
    console.log(remoteUrl)
    if (remoteUrl) {
      setCurrentStep(2);
      return;
    }
    setCurrentStep(1);
    setSelectedFile(file);
    // 在校验过程中去处理文件上传
    try {
      await upload({ file });
      setCurrentStep(2);
    } catch (error) {
      console.error('上传文件失败', error);
      setCurrentStep(0);
    }
  };

  // 重新上传文件
  const handleReUpload = (file: File) => {
    setCurrentStep(0);
    setSelectedFile(file);
  };

  // 更改表单
  const handleChange = (data: { actorName: string; voiceId: string }) => {
    // changeActorName(data.actorName);
    if (data.actorName && data.voiceId) {
      setCurrentStep(3);
    } else {
      setCurrentStep(2);
    }
  };

  // 创建演员
  const handleCreate = async () => {
    //调用回调返回相关数据
    // onCreate
    onCreateSuccess?.();
    handleClose();
  };

  // 重置状态
  const handleClose = () => {
    setCurrentStep(0);
    setSelectedFile(null);
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  const originFileURL = useMemo(() => {
    return selectedFile ? URL.createObjectURL(selectedFile) : "";
  }, [selectedFile])

  // 弹窗内容
  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[748px] h-[615px] max-w-[90vw] max-h-[90vh]",
          "z-50"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-5 px-6 py-4 border-b">
          <Dialog.Title className="text-i-normal text-lg font-semibold">
            {uploadType === "image" ? t("custom_digital_human.photo_to_actor") : t("custom_digital_human.video_to_actor")}
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4" />
            </button>
          </Dialog.Close>
        </div>

        {/* 步骤指示器 */}
        <StepIndicator className="mb-8" currentStep={currentStep} />

        {/* 内容区域 */}
        {currentStep === 0 && (
          <UploadForm
            className="flex-1 mb-8"
            uploadType={uploadType}
            file={selectedFile}
            remoteUrl={remoteUrl}
            onSubmit={handleUpload}
          />
        )}
        {currentStep === 1 && (
          <VerifyForm
            className="flex-1 mb-8"
            uploadType={uploadType}
            URL={originFileURL || remoteUrl}
          />
        )}
        {currentStep >= 2 && (
          <CreateForm
            className="flex-1 mb-8"
            uploadType={uploadType}
            file={selectedFile}
            URL={originFileURL || remoteUrl}
            onReUpload={handleReUpload}
            onChange={handleChange}
            onSubmit={handleCreate}
          />
        )}
      </Dialog.Content>
    </Dialog.Portal>
  );

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
};
